import React, { useState, useRef, useMemo, useCallback, ReactNode } from 'react'
import { Box, Typography, IconButton, Tooltip, TextField } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import ZoomInIcon from '@mui/icons-material/ZoomIn'
import ZoomOutIcon from '@mui/icons-material/ZoomOut'
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import AddIcon from '@mui/icons-material/Add'
import DragIndicatorIcon from '@mui/icons-material/DragIndicator'
import CallMergeIcon from '@mui/icons-material/CallMerge'
import { FancyButton } from './FancyButtons'
import { FS_SM, FS_XS, FS_MD, ICON_SM, ICON_MD } from '../common/theme'
import type { DocPage, DocPageElement, ExtractedQuestion, ExtractQuestionsWebResult } from '../types'

// Shared list-item shape used by both review (Phase 2) and organize
// (Phase 3) phases - a question reference or a section divider. Exported
// so the organize phase can consume the same type.
export type FlatItem =
  | { type: 'section'; sectionId: string }
  | { type: 'question'; sha: string }

function randomHex(len = 16): string {
  const arr = new Uint8Array(len / 2)
  crypto.getRandomValues(arr)
  return Array.from(arr).map(b => b.toString(16).padStart(2, '0')).join('')
}

interface BiqReviewPhaseProps {
  extractResult: ExtractQuestionsWebResult
  questions: ExtractedQuestion[]
  setQuestions: React.Dispatch<React.SetStateAction<ExtractedQuestion[]>>
  items: FlatItem[]
  setItems: React.Dispatch<React.SetStateAction<FlatItem[]>>
  qMap: Record<string, ExtractedQuestion>
  acceptedCount: number
  isUploading: boolean
  onNext: () => void
  onCancel: () => void
}

// Phase 2 of the bid-import wizard. Side-by-side thumbnail of the source
// document and the extracted-question list. Owns its own zoom / page-nav /
// hover / numbering-edit / drag-drop state - only the durable model
// (questions + items) is shared with the parent so it survives the
// review→organize transition.
export default function BiqReviewPhase({
  extractResult, questions, setQuestions, items, setItems, qMap,
  acceptedCount, isUploading, onNext, onCancel,
}: BiqReviewPhaseProps) {
  const [activePage, setActivePage] = useState(
    extractResult.firstactivepage && extractResult.firstactivepage > 0
      ? extractResult.firstactivepage
      : (extractResult.docpages[0]?.pagenum ?? 1)
  )
  const [zoom, setZoom] = useState(1)
  const [hoverEl, setHoverEl] = useState<DocPageElement | null>(null)

  const [numberingEditSha, setNumberingEditSha] = useState<string | null>(null)
  const [numberingDraft, setNumberingDraft] = useState('')

  // Pages of questions added after extraction (thumbnail click / blank add)
  // that aren't in the docpages map.
  const [extraPages, setExtraPages] = useState<Record<string, number>>({})

  // Brief highlight flash on a freshly-added question
  const [recentlyAddedSha, setRecentlyAddedSha] = useState<string | null>(null)
  const flashTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Question-list scroll syncing with thumbnail
  const questionListRef = useRef<HTMLDivElement>(null)
  const rowRefs = useRef(new Map<string, HTMLElement>())
  const scrollingPageRef = useRef(false)
  const scrollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const setRowRef = (sha: string, el: HTMLElement | null) => {
    if (el) rowRefs.current.set(sha, el)
    else rowRefs.current.delete(sha)
  }

  const dragIdxRef = useRef<number | null>(null)
  const [overIdx, setOverIdx] = useState<number | null>(null)

  // Map sha -> pagenum for thumbnail navigation
  const shaToPage = useMemo(() => {
    const m = new Map<string, number>()
    for (const p of extractResult.docpages) {
      for (const q of p.questions) m.set(q.questionsha, p.pagenum)
    }
    return m
  }, [extractResult])

  const curPage: DocPage | undefined = extractResult.docpages.find(p => p.pagenum === activePage)
  const totalPages = extractResult.docpages.length

  const getPage = useCallback((sha: string): number | undefined => {
    return extraPages[sha] ?? shaToPage.get(sha)
  }, [extraPages, shaToPage])

  const flashRow = useCallback((sha: string) => {
    setRecentlyAddedSha(sha)
    if (flashTimerRef.current) clearTimeout(flashTimerRef.current)
    flashTimerRef.current = setTimeout(() => setRecentlyAddedSha(null), 1400)
  }, [])

  // Ordinal among included questions (regardless of section dividers).
  const ordinalMap = useMemo(() => {
    const m: Record<string, number> = {}
    let n = 0
    for (const item of items) {
      if (item.type === 'question') {
        const q = qMap[item.sha]
        if (q && q.include) m[item.sha] = ++n
      }
    }
    return m
  }, [items, qMap])

  const selectedCount = useMemo(() => questions.filter(q => q.selected).length, [questions])

  // Compute the insertion index in `items` for a question on (page, ymin, xmin).
  const insertIdxForPosition = useCallback((page: number, ymin: number, xmin: number): number => {
    for (let i = 0; i < items.length; i++) {
      const it = items[i]
      if (it.type !== 'question') continue
      const itPage = getPage(it.sha)
      if (itPage === undefined) continue
      if (itPage > page) return i
      if (itPage < page) continue
      const el = qMap[it.sha]?.element
      if (!el) continue
      if (el.ymin > ymin || (el.ymin === ymin && el.xmin > xmin)) return i
    }
    return items.length
  }, [items, qMap, getPage])

  // Union of element bounding boxes (merge action keeps the resulting bbox).
  const mergeElements = (els: DocPageElement[]): DocPageElement | null => {
    let xmin = Infinity, ymin = Infinity, xmax = -Infinity, ymax = -Infinity
    let count = 0
    for (const e of els) {
      if (!e) continue
      xmin = Math.min(xmin, e.xmin)
      ymin = Math.min(ymin, e.ymin)
      xmax = Math.max(xmax, e.xmin + e.width)
      ymax = Math.max(ymax, e.ymin + e.height)
      count++
    }
    if (count === 0) return null
    return {
      xmin, ymin,
      width: xmax - xmin,
      height: ymax - ymin,
      id: els[0].id,
      Content: els.map(e => e.Content ?? '').filter(Boolean).join('\n'),
      type: 'para',
    }
  }

  const toggleSelected = useCallback((sha: string) => {
    setQuestions(prev => prev.map(q => q.questionsha === sha ? { ...q, selected: !q.selected } : q))
  }, [setQuestions])

  const clearSelected = useCallback(() => {
    setQuestions(prev => prev.map(q => q.selected ? { ...q, selected: false } : q))
  }, [setQuestions])

  const handleMerge = useCallback(() => {
    const selectedShas: string[] = []
    for (const it of items) {
      if (it.type === 'question' && qMap[it.sha]?.selected) selectedShas.push(it.sha)
    }
    if (selectedShas.length < 2) return

    const firstSha = selectedShas[0]
    const firstQ = qMap[firstSha]
    if (!firstQ) return
    const firstPage = getPage(firstSha)

    const mergedText = selectedShas
      .map(s => qMap[s]?.question ?? '')
      .filter(Boolean)
      .join('<br>')

    const sameFirstPage = selectedShas
      .filter(s => getPage(s) === firstPage)
      .map(s => qMap[s]?.element)
      .filter(Boolean) as DocPageElement[]
    const mergedEl = mergeElements(sameFirstPage) ?? firstQ.element ?? null

    const dropSet = new Set(selectedShas.filter(s => s !== firstSha))

    setQuestions(prev =>
      prev
        .filter(q => !dropSet.has(q.questionsha))
        .map(q => q.questionsha === firstSha
          ? { ...q, question: mergedText, element: mergedEl, selected: false, include: true }
          : q)
    )
    setItems(prev => prev.filter(it => it.type !== 'question' || !dropSet.has(it.sha)))
    flashRow(firstSha)
  }, [items, qMap, getPage, flashRow, setQuestions, setItems])

  const updateQuestion = useCallback((sha: string, patch: Partial<ExtractedQuestion>) => {
    setQuestions(prev => prev.map(q => q.questionsha === sha ? { ...q, ...patch } : q))
  }, [setQuestions])

  const deleteQuestion = useCallback((sha: string) => {
    setQuestions(prev => prev.filter(q => q.questionsha !== sha))
    setItems(prev => prev.filter(i => !(i.type === 'question' && i.sha === sha)))
  }, [setQuestions, setItems])

  const addBlankQuestion = useCallback(() => {
    const sha = randomHex()
    const newQ: ExtractedQuestion = {
      questionsha: sha,
      question: '',
      numbering: '✱',
      include: true,
      state: 'new',
      element: null,
    }
    const idx = insertIdxForPosition(activePage, Number.POSITIVE_INFINITY, Number.POSITIVE_INFINITY)
    setQuestions(prev => [...prev, newQ])
    setItems(prev => {
      const next = [...prev]
      next.splice(idx, 0, { type: 'question', sha })
      return next
    })
    setExtraPages(prev => ({ ...prev, [sha]: activePage }))
    setTimeout(() => rowRefs.current.get(sha)?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 60)
    flashRow(sha)
  }, [activePage, insertIdxForPosition, flashRow, setQuestions, setItems])

  // DnD on flat list
  const handleDragStart = (e: React.DragEvent, idx: number) => {
    dragIdxRef.current = idx
    e.dataTransfer.effectAllowed = 'move'
    e.dataTransfer.setData('text/plain', String(idx))
  }
  const handleDragOver = (e: React.DragEvent, idx: number) => {
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
    if (dragIdxRef.current !== idx) setOverIdx(idx)
  }
  const handleDropItem = (e: React.DragEvent, toIdx: number) => {
    e.preventDefault()
    const fromIdx = dragIdxRef.current
    if (fromIdx === null || fromIdx === toIdx) { setOverIdx(null); return }
    setItems(prev => {
      const next = [...prev]
      const [moved] = next.splice(fromIdx, 1)
      next.splice(toIdx > fromIdx ? toIdx - 1 : toIdx, 0, moved)
      return next
    })
    dragIdxRef.current = null
    setOverIdx(null)
  }
  const handleDragEnd = () => { dragIdxRef.current = null; setOverIdx(null) }

  // Suppress scroll-driven page sync briefly after a programmatic action
  // (otherwise jumping to a row would trigger scroll → setActivePage → loop).
  const lockScrollSync = () => {
    scrollingPageRef.current = true
    if (scrollTimerRef.current) clearTimeout(scrollTimerRef.current)
    scrollTimerRef.current = setTimeout(() => { scrollingPageRef.current = false }, 800)
  }

  const handleShow = (sha: string) => {
    const p = getPage(sha)
    if (p) setActivePage(p)
    lockScrollSync()
    rowRefs.current.get(sha)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  const goToPage = (newPage: number) => {
    if (newPage < 1 || newPage > totalPages) return
    setActivePage(newPage)
    lockScrollSync()
    for (const item of items) {
      if (item.type === 'question' && getPage(item.sha) === newPage) {
        rowRefs.current.get(item.sha)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
        break
      }
    }
  }

  const handleQuestionScroll = useCallback(() => {
    if (scrollingPageRef.current) return
    const container = questionListRef.current
    if (!container) return
    const containerTop = container.getBoundingClientRect().top + 8
    for (const item of items) {
      if (item.type !== 'question') continue
      const el = rowRefs.current.get(item.sha)
      if (!el) continue
      const rect = el.getBoundingClientRect()
      if (rect.bottom > containerTop) {
        const p = getPage(item.sha)
        if (p && p !== activePage) setActivePage(p)
        break
      }
    }
  }, [items, getPage, activePage])

  const addFromElement = (el: DocPageElement) => {
    const sha = randomHex()
    const newQ: ExtractedQuestion = {
      questionsha: sha,
      question: el.Content ?? '',
      numbering: '✱',
      include: true,
      state: 'new',
      element: el,
    }
    const idx = insertIdxForPosition(activePage, el.ymin, el.xmin)
    setQuestions(prev => [...prev, newQ])
    setItems(prev => {
      const next = [...prev]
      next.splice(idx, 0, { type: 'question', sha })
      return next
    })
    setExtraPages(prev => ({ ...prev, [sha]: activePage }))
    lockScrollSync()
    setTimeout(() => rowRefs.current.get(sha)?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 60)
    flashRow(sha)
  }

  const isElementAddable = (el: DocPageElement) => {
    return !questions.some(q => q.include && q.element && q.element.id === el.id)
  }

  return (
    <Box sx={{ display: 'flex', flex: 1, minHeight: 0 }}>

      {/* Left pane: thumbnail */}
      <Box sx={{ width: '50%', display: 'flex', flexDirection: 'column', borderRight: 1, borderColor: 'divider', minHeight: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 1, py: 0.5, borderBottom: 1, borderColor: 'divider', flexShrink: 0 }}>
          <Tooltip title="Previous page">
            <span>
              <IconButton data-testid="biq_prev_btn" size="small" disabled={activePage <= 1} onClick={() => goToPage(activePage - 1)}>
                <ChevronLeftIcon sx={{ fontSize: ICON_MD }} />
              </IconButton>
            </span>
          </Tooltip>
          <Typography data-testid="biq_pagelabel" variant="caption" sx={{ fontSize: FS_MD }}>
            Page {curPage?.pagenum ?? '-'} of {totalPages}
          </Typography>
          <Tooltip title="Next page">
            <span>
              <IconButton data-testid="biq_next_btn" size="small" disabled={activePage >= totalPages} onClick={() => goToPage(activePage + 1)}>
                <ChevronRightIcon sx={{ fontSize: ICON_MD }} />
              </IconButton>
            </span>
          </Tooltip>
          <Box sx={{ flex: 1 }} />
          <Tooltip title="Zoom out">
            <IconButton data-testid="biq_zoomout_btn" size="small" onClick={() => setZoom(z => Math.max(0.4, z - 0.1))}>
              <ZoomOutIcon sx={{ fontSize: ICON_MD }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Zoom in">
            <IconButton data-testid="biq_zoomin_btn" size="small" onClick={() => setZoom(z => Math.min(3, z + 0.1))}>
              <ZoomInIcon sx={{ fontSize: ICON_MD }} />
            </IconButton>
          </Tooltip>
        </Box>

        <Box sx={{ flex: 1, overflow: 'auto', bgcolor: 'action.hover', p: 1, display: 'flex', justifyContent: 'center', alignItems: 'flex-start' }}>
          {curPage && (
            <Box sx={{ position: 'relative', transform: `scale(${zoom})`, transformOrigin: 'top center' }}>
              <Box sx={{ position: 'relative', display: 'inline-block' }}>
                {curPage.thumb && (
                  <img src={curPage.thumb} alt={`Page ${curPage.pagenum}`} style={{ display: 'block', maxWidth: '100%' }} />
                )}
                <svg
                  viewBox={curPage.viewbox}
                  preserveAspectRatio="none"
                  style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none' }}
                >
                  {/* Addable elements (faint, click to add) */}
                  {curPage.elements?.filter(isElementAddable).map(el => (
                    <rect
                      key={el.id}
                      x={el.xmin} y={el.ymin} width={el.width} height={el.height}
                      rx={0.005} ry={0.005}
                      fill="rgba(0,0,0,0)" stroke="#999" strokeWidth={0.002}
                      strokeDasharray="0.005,0.005"
                      style={{ pointerEvents: 'all', cursor: 'pointer' }}
                      onMouseEnter={() => setHoverEl(el)}
                      onMouseLeave={() => setHoverEl(null)}
                      onClick={() => addFromElement(el)}
                    />
                  ))}
                  {/* Included questions for this page */}
                  {questions
                    .filter(x => x.include && x.element && getPage(x.questionsha) === curPage.pagenum)
                    .map(x => x.element && (
                      <rect
                        key={x.questionsha}
                        x={x.element.xmin} y={x.element.ymin} width={x.element.width} height={x.element.height}
                        rx={0.005} ry={0.005}
                        fill={recentlyAddedSha === x.questionsha ? 'rgba(76, 175, 80, 0.28)' : 'rgba(25, 118, 210, 0.12)'}
                        stroke={recentlyAddedSha === x.questionsha ? '#43a047' : '#1976d2'}
                        strokeWidth={recentlyAddedSha === x.questionsha ? 0.006 : 0.003}
                        style={{ transition: 'fill 0.45s, stroke 0.45s, stroke-width 0.45s' }}
                      />
                    ))
                  }
                  {hoverEl && (
                    <rect
                      x={hoverEl.xmin} y={hoverEl.ymin} width={hoverEl.width} height={hoverEl.height}
                      rx={0.005} ry={0.005}
                      fill="rgba(255, 193, 7, 0.18)" stroke="#f57c00" strokeWidth={0.003}
                    />
                  )}
                </svg>
              </Box>
            </Box>
          )}
        </Box>
      </Box>

      {/* Right pane: questions */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 1, py: 0.5, borderBottom: 1, borderColor: 'divider', flexShrink: 0 }}>
          <Typography data-testid="biq_count" variant="caption" sx={{ fontWeight: 600, fontSize: FS_SM }}>
            {acceptedCount} {acceptedCount === 1 ? 'question' : 'questions'}
          </Typography>
          {selectedCount > 0 && (
            <>
              <Typography data-testid="biq_selected_count" variant="caption" sx={{ fontSize: FS_XS, color: 'warning.main', fontWeight: 600, ml: 0.5 }}>
                ({selectedCount} selected)
              </Typography>
              <Tooltip title={selectedCount < 2 ? 'Select two or more to merge' : 'Merge selected questions'}>
                <span>
                  <IconButton
                    data-testid="biq_merge_btn"
                    size="small"
                    disabled={selectedCount < 2}
                    onClick={handleMerge}
                    sx={{ color: 'warning.main' }}
                  >
                    <CallMergeIcon sx={{ fontSize: ICON_MD }} />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Clear selection">
                <IconButton data-testid="biq_clear_selection_btn" size="small" onClick={clearSelected}>
                  <CloseIcon sx={{ fontSize: ICON_MD }} />
                </IconButton>
              </Tooltip>
            </>
          )}
          <Box sx={{ flex: 1 }} />
          <Tooltip title="Add Question">
            <IconButton data-testid="biq_addquestion_btn" size="small" onClick={addBlankQuestion}>
              <AddIcon sx={{ fontSize: ICON_MD }} />
            </IconButton>
          </Tooltip>
        </Box>

        <Box ref={questionListRef} onScroll={handleQuestionScroll}
          data-testid="biq_qlist"
          sx={{ flex: 1, overflowY: 'auto', px: 0.5, position: 'relative' }}>
          {(() => {
            const rendered: ReactNode[] = []
            let lastPage: number | null = null
            for (let idx = 0; idx < items.length; idx++) {
              const item = items[idx]
              const isDropTarget = overIdx === idx && dragIdxRef.current !== null && dragIdxRef.current !== idx

              // Section management lives in the organize phase only.
              if (item.type === 'section') continue

              const q = qMap[item.sha]
              if (!q) continue
              const qPage = getPage(item.sha)

              // Page-group sticky pseudo header (auto-derived, not draggable)
              if (qPage !== undefined && qPage !== lastPage) {
                rendered.push(
                  <Box
                    key={`pageheader-${qPage}-${idx}`}
                    data-testid="biq_pageheader"
                    data-page={qPage}
                    sx={{
                      position: 'sticky', top: 0, zIndex: 2,
                      display: 'flex', alignItems: 'center', gap: 0.5,
                      px: 0.75, py: 0.4,
                      bgcolor: 'background.paper',
                      borderBottom: 1, borderColor: 'divider',
                      opacity: 0.92,
                    }}
                  >
                    <Typography variant="caption" sx={{ fontSize: FS_MD, fontWeight: 700, color: 'text.secondary', letterSpacing: 0.4, textTransform: 'uppercase' }}>
                      Page {qPage}
                    </Typography>
                  </Box>
                )
                lastPage = qPage
              }

              const onCurrentPage = qPage === activePage
              const ordinal = ordinalMap[item.sha]
              const hasNumbering = !!q.numbering && q.numbering !== '✱'
              const isEditingNum = numberingEditSha === q.questionsha

              const isFlashing = recentlyAddedSha === item.sha
              const isSelected = !!q.selected
              rendered.push(
                <Box
                  key={item.sha}
                  ref={(el: HTMLElement | null) => setRowRef(item.sha, el)}
                  data-testid="biq_question_row"
                  data-sha={item.sha}
                  data-selected={isSelected ? 'true' : 'false'}
                  draggable
                  onDragStart={e => handleDragStart(e, idx)}
                  onDragOver={e => handleDragOver(e, idx)}
                  onDrop={e => handleDropItem(e, idx)}
                  onDragEnd={handleDragEnd}
                  onMouseEnter={() => q.element && onCurrentPage && setHoverEl(q.element)}
                  onMouseLeave={() => setHoverEl(null)}
                  sx={{
                    display: 'flex', alignItems: 'flex-start', gap: 0.5,
                    px: 0.75, py: 0.5,
                    borderTop: isDropTarget ? '3px solid' : '3px solid transparent',
                    borderTopColor: isDropTarget ? 'warning.main' : 'transparent',
                    borderBottom: 1, borderColor: 'divider',
                    opacity: q.include ? 1 : 0.45,
                    bgcolor: isFlashing
                      ? 'rgba(76, 175, 80, 0.22)'
                      : isSelected
                        ? 'rgba(255, 193, 7, 0.12)'
                        : undefined,
                    transition: 'background-color 0.45s',
                    '&:hover': { bgcolor: isSelected ? 'rgba(255, 193, 7, 0.18)' : 'action.hover' },
                  }}
                >
                  <Tooltip title={isSelected ? 'Deselect' : 'Select for merge'}>
                    <Box
                      data-testid="biq_question_select"
                      onClick={() => toggleSelected(q.questionsha)}
                      sx={{
                        mt: '6px', flexShrink: 0,
                        width: 14, height: 14, borderRadius: '3px',
                        border: 1.5, borderColor: isSelected ? 'warning.main' : 'divider',
                        bgcolor: isSelected ? 'warning.main' : 'transparent',
                        cursor: 'pointer',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        '&:hover': { borderColor: 'warning.main' },
                      }}
                    >
                      {isSelected && <Box sx={{ width: 6, height: 6, bgcolor: '#fff', borderRadius: '1px' }} />}
                    </Box>
                  </Tooltip>
                  <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0, mt: '6px' }}>
                    {ordinal != null && (
                      <Typography data-testid="biq_question_ordinal" sx={{
                        fontSize: FS_MD, lineHeight: 1,
                        fontWeight: 400,
                        color: 'text.disabled',
                      }}>
                        {ordinal}
                      </Typography>
                    )}
                  </Box>
                  <DragIndicatorIcon sx={{ fontSize: ICON_SM, opacity: 0.3, mt: '4px', flexShrink: 0, cursor: 'grab' }} />
                  <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0, mt: '2px' }}>
                    {q.element ? (
                      <Tooltip title="Show on page">
                        <IconButton data-testid="biq_question_show_btn" size="small" onClick={() => handleShow(q.questionsha)} sx={{ p: 0.25 }}>
                          <VisibilityOutlinedIcon sx={{ fontSize: ICON_SM }} />
                        </IconButton>
                      </Tooltip>
                    ) : (
                      <Box sx={{ width: ICON_SM, height: ICON_SM, p: 0.25 }} />
                    )}
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'baseline', gap: '4px', flexShrink: 0, mt: '6px', minWidth: 36 }}>
                    {isEditingNum ? (
                      <TextField
                        size="small"
                        autoFocus
                        value={numberingDraft}
                        onChange={e => setNumberingDraft(e.target.value)}
                        onKeyDown={e => {
                          if (e.key === 'Enter') {
                            updateQuestion(q.questionsha, { numbering: numberingDraft.trim() || undefined })
                            setNumberingEditSha(null)
                          }
                          if (e.key === 'Escape') setNumberingEditSha(null)
                        }}
                        onBlur={() => {
                          updateQuestion(q.questionsha, { numbering: numberingDraft.trim() || undefined })
                          setNumberingEditSha(null)
                        }}
                        inputProps={{ 'data-testid': 'biq_question_numbering_input' }}
                        sx={{ width: 60, '& .MuiInputBase-input': { py: 0.15, px: 0.4, fontSize: FS_SM } }}
                      />
                    ) : (
                      <Typography
                        data-testid="biq_question_numbering"
                        onClick={() => { setNumberingEditSha(q.questionsha); setNumberingDraft(q.numbering ?? '') }}
                        sx={{
                          fontSize: FS_MD, lineHeight: 1, cursor: 'text',
                          fontWeight: 700,
                          color: hasNumbering ? 'text.primary' : 'text.disabled',
                          '&:hover': { color: 'primary.main' },
                          minWidth: 14, display: 'inline-block',
                        }}
                      >
                        {hasNumbering ? q.numbering : '+'}
                      </Typography>
                    )}
                  </Box>

                  <TextField
                    multiline
                    minRows={1}
                    maxRows={6}
                    value={q.question}
                    onChange={e => updateQuestion(q.questionsha, { question: e.target.value })}
                    variant="standard"
                    sx={{ flex: 1, '& .MuiInputBase-input': { fontSize: FS_SM, lineHeight: 1.4 } }}
                    InputProps={{ disableUnderline: true }}
                    inputProps={{ 'data-testid': 'biq_question_input' }}
                    placeholder="Question text..."
                  />
                  <Tooltip title={q.include ? 'Exclude' : 'Include'}>
                    <IconButton data-testid="biq_question_include_btn" size="small" onClick={() => updateQuestion(q.questionsha, { include: !q.include })} sx={{ p: 0.25 }}>
                      {q.include ? <CloseIcon sx={{ fontSize: ICON_SM }} /> : <AddIcon sx={{ fontSize: ICON_SM }} />}
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete">
                    <IconButton data-testid="biq_question_delete_btn" size="small" onClick={() => deleteQuestion(q.questionsha)} sx={{ p: 0.25 }}>
                      <DeleteOutlineIcon sx={{ fontSize: ICON_SM }} />
                    </IconButton>
                  </Tooltip>
                </Box>
              )
            }
            return rendered
          })()}
        </Box>

        <Box sx={{ display: 'flex', gap: 1, p: 1, borderTop: 1, borderColor: 'divider', flexShrink: 0 }}>
          <Box sx={{ flex: 1 }} />
          <FancyButton testId="biq_cancel_btn" onClick={onCancel} disabled={isUploading}>Cancel</FancyButton>
          <FancyButton
            testId="biq_next_btn"
            solid
            onClick={onNext}
            disabled={acceptedCount === 0}
          >
            {`Next: Organize ${acceptedCount}`}
          </FancyButton>
        </Box>
      </Box>
    </Box>
  )
}
